import { setRestedBonusElementVariant } from './rested-bonus-policy.js';
import { createNumberTicker } from './number-ticker.js';
import { syncPrivateMysteryProductAvailability } from './private-mystery-hold.js';
import { setRestedBonusVariant } from './loyalty-rested-status.js?v=20261020-rested-delay';
import { formatProductQuantityTotal, getRecentlyViewedPrice } from './product-price-context.js';
import {
  BUNDLE_LABEL_SWAP_MOTION,
  createMeasuredLabelSwap,
} from './product-button-label-swap.js';

const PRODUCT_SELECTOR = '[data-product-section]:not([data-quick-buy-card])';
const RECENTLY_VIEWED_STORAGE_KEY = 'sigma-recently-viewed-products';
const LEGACY_RECENTLY_VIEWED_STORAGE_KEY = 'sigma-recently-viewed-coffees';
const RECENTLY_VIEWED_LIMIT = 8;
const RECENTLY_VIEWED_RENDER_LIMIT = 6;

const FELLOW_BREADCRUMB_PATHS = Object.freeze({
  'fellow-aiden': '/collections/xbloom',
  'fellow-opus-2': '/collections/grinders',
});

const initializeFellowBreadcrumb = (root) => {
  const productHandle = root.dataset.fellowProduct;
  const categoryPath = FELLOW_BREADCRUMB_PATHS[productHandle];
  const categoryCrumb = root.querySelector('[data-fellow-breadcrumb-category]');
  const brandCrumb = root.querySelector('[data-fellow-breadcrumb-brand]');

  if (!categoryPath || !categoryCrumb || !brandCrumb) {
    return;
  }

  const navigationSource = new URL(window.location.href).searchParams.get('navigation');
  let arrivedFromFellowDestination = false;

  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      const normalizedPath = referrer.pathname.replace(/\/+$/, '');
      const fellowVendorFilter = referrer.searchParams
        .getAll('filter.p.vendor')
        .some((value) => value.trim().toLowerCase() === 'fellow');
      const fellowVendorDestination =
        normalizedPath === '/collections/vendors' &&
        referrer.searchParams.getAll('q').some((value) => value.trim().toLowerCase() === 'fellow');
      const fellowCategoryDestination = normalizedPath === categoryPath && fellowVendorFilter;

      arrivedFromFellowDestination =
        referrer.origin === window.location.origin &&
        (normalizedPath === '/collections/fellow' || fellowVendorDestination || fellowCategoryDestination);
    } catch {
      // A malformed or unavailable referrer leaves the server-rendered category trail unchanged.
    }
  }

  if (navigationSource === 'fellow' || arrivedFromFellowDestination) {
    categoryCrumb.hidden = true;
    brandCrumb.hidden = false;
  }
};

const plainTextFromMarkup = (value) => {
  const text = String(value || '');

  if (!text.includes('<') && !text.includes('&')) {
    return text;
  }

  return new DOMParser().parseFromString(text, 'text/html').body.textContent || '';
};

/* ─────────────────────────────────────────────────────────
 * VARIANT STATE SWAP STORYBOARD
 *
 *    0ms   outgoing price and stock state lift, blur and fade
 *  140ms   content swaps below its resting position
 *  280ms   incoming state settles sharp and fully opaque
 * ───────────────────────────────────────────────────────── */
const VARIANT_SWAP_TIMING = Object.freeze({
  phase: 140,
});

const variantSwapStates = new WeakMap();

const BUNDLE_PURCHASE_LABEL_MOTION = BUNDLE_LABEL_SWAP_MOTION;
const swapBundlePurchaseLabel = createMeasuredLabelSwap({
  containerClass: 'coffee-product__bundle-purchase-label',
  valueClass: 'coffee-product__bundle-purchase-label-value',
  motion: BUNDLE_PURCHASE_LABEL_MOTION,
});

/* ─────────────────────────────────────────────────────────
 * QUANTITY STEPPER STORYBOARD
 *
 *    0ms   pointer down: step once and press surface settles
 *  180ms   outgoing number rolls away, incoming number settles
 *  340ms   held press enters repeating stage
 *  340ms+  repeat interval accelerates 155ms to 72ms
 * 1940ms   maximum repeat speed reached
 * ───────────────────────────────────────────────────────── */
const QUANTITY_STEPPER_TIMING = Object.freeze({
  digitRoll: 180,
  holdDelay: 340,
  repeatStartInterval: 155,
  repeatMinInterval: 72,
  repeatAccelerationDuration: 1600,
  repeatRollFloor: 64,
  repeatedDurationRatio: 0.82,
});

const QUANTITY_HOLD_STAGE = Object.freeze({
  idle: 0,
  pressed: 1,
  repeating: 2,
});

/* ─────────────────────────────────────────────────────────
 * SHARED MEDIA DIALOG STORYBOARD
 *
 *    0ms   dialog mounts; card matches the gallery and the glass lens keeps its size
 *   40ms   minimise icon resolves through scale, blur and opacity
 *  340ms   card settles in the viewport centre with the backdrop fully resolved
 *  340ms   previous and next controls begin fading in after the card settles
 *  500ms   previous and next controls reach full opacity
 *    Esc   an interrupted entrance reverses from its current position
 *    0ms   previous and next controls begin fading out on shrink
 *  260ms   settled dialog returns to the gallery and restores trigger focus
 * ───────────────────────────────────────────────────────── */
const MEDIA_DIALOG_MOTION = Object.freeze({
  openDuration: 340,
  closeDuration: 260,
  openEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  closeEasing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  iconDuration: 240,
  iconDelay: 40,
  navigationEnterDuration: 160,
  navigationExitDuration: 120,
  iconBlur: 4,
  iconScale: 0.25,
  lensRestingOpacity: 1,
  lensSharedOpacity: 0.92,
  minimumCounterScale: 0.72,
  maximumCounterScale: 1.48,
});

const MEDIA_DIALOG_LOADING = Object.freeze({
  revealDelay: 160,
});

const MEDIA_CONTROL_TONE = Object.freeze({
  sampleCanvasSize: 64,
  minimumSampleSize: 4,
  visibleLensSize: 36,
  darkMediaThreshold: 0.56,
});

const GALLERY_SWIPE = Object.freeze({
  minimumDistance: 44,
  minimumFlingDistance: 24,
  velocityThreshold: 0.35,
  directionRatio: 1.2,
  edgeResistance: 0.18,
  settleDuration: 260,
  minimumSettleDuration: 140,
  returnDuration: 180,
  reducedMotionDuration: 140,
  easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
});

/* ─────────────────────────────────────────────────────────
 * DESCRIPTION DISCLOSURE STORYBOARD
 *
 *    0ms   toggle state and chevron respond to the user
 *  180ms   button label and chevron finish resolving
 *  260ms   description reaches its expanded or block-aware preview height
 *  260ms   collapse restores the focused toggle to its prior viewport position
 * ───────────────────────────────────────────────────────── */
const DESCRIPTION_MOTION = Object.freeze({
  targetCollapsedHeight: 340,
  blockSnapRange: 64,
  minimumOverflow: 56,
  duration: 260,
  easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
});

/* ─────────────────────────────────────────────────────────
 * STICKY PURCHASE STORYBOARD
 *
 *    0ms   the original purchase flow remains the only active form surface
 *  240ms   mobile shortcut rises from the bottom and settles fully opaque
 *  220ms   exit fades and retreats 24px before the hidden position is restored
 * 1024px+  bundle products retain the shortcut while customers configure add-ons
 * ───────────────────────────────────────────────────────── */
const STICKY_PURCHASE_STAGE = Object.freeze({
  hidden: 0,
  visible: 1,
  revealOffset: 8,
  hideHysteresis: 24,
});

const STICKY_PURCHASE_MOTION = Object.freeze({
  exitDuration: 220,
  exitDistance: 24,
});

const STICKY_PURCHASE_DRAG = Object.freeze({
  distanceRatio: 0.32,
  maximumDistance: 72,
  minimumFlingDistance: 24,
  velocityThreshold: 0.25,
});

const parseVariants = (root) => {
  const source = root.querySelector('[data-product-variants]');

  if (!source) {
    return [];
  }

  try {
    return JSON.parse(source.textContent);
  } catch (error) {
    return [];
  }
};

const selectedOptions = (root) => {
  const options = [];

  root.querySelectorAll('[data-option-input]:checked').forEach((input) => {
    const position = Number(input.closest('[data-option-position]')?.dataset.optionPosition);

    if (Number.isInteger(position)) {
      options[position] = input.value;
    }
  });

  return options;
};

const syncFinishSelectedValues = (root, variant) => {
  const privateHold = root.hasAttribute('data-private-mystery-hold');
  root.querySelectorAll('[data-option-position]').forEach((group) => {
    const selectedValue = group.querySelector('[data-option-input]:checked')?.value;
    const output = group.querySelector('[data-finish-selected-value]');

    if (output && selectedValue) {
      output.textContent = selectedValue;
      const soldOut = group.querySelector('[data-finish-sold-out]');
      if (soldOut) soldOut.hidden = !variant || variant.available || privateHold;
    }
  });
};

const optionsMatch = (variant, options) =>
  variant.options.length === options.length &&
  variant.options.every((option, index) => option === options[index]);

const resolveSelectedVariant = (root, variants) => {
  // Shopify's default variant has no customer-facing option controls.
  if (
    variants.length === 1 &&
    root.querySelector('[data-variant-select][data-default-variant]') &&
    !root.querySelector('[data-option-input]')
  ) {
    return variants[0];
  }

  return variants.find((candidate) => optionsMatch(candidate, selectedOptions(root)));
};

const parseWeightInGrams = (value) => {
  const normalized = String(value).trim().toLowerCase().replace(',', '.');
  const amount = Number.parseFloat(normalized);

  if (!Number.isFinite(amount)) {
    return Number.POSITIVE_INFINITY;
  }

  if (normalized.includes('kg')) {
    return amount * 1000;
  }

  if (normalized.includes('lb')) {
    return amount * 453.592;
  }

  if (normalized.includes('oz')) {
    return amount * 28.3495;
  }

  return amount;
};

const parseRoastDate = (value) => {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  const normalized = String(value).replace(/(\d+)(st|nd|rd|th)/gi, '$1');
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const getRoastDateSortValue = (variants, position, optionValue) => {
  const timestamps = variants
    .filter((variant) => variant.options[position] === optionValue)
    .map((variant) => parseRoastDate(variant.roastDate))
    .filter(Number.isFinite);

  return timestamps.length ? Math.min(...timestamps) : parseRoastDate(optionValue);
};

const sortProductOptionValues = (root, variants) => {
  let orderChanged = false;

  root.querySelectorAll('[data-option-position]').forEach((group) => {
    const optionName = group.dataset.optionName?.trim().toLowerCase() || '';
    const position = Number(group.dataset.optionPosition);
    const values = group.querySelector('.coffee-product__option-values');

    if (!values || !Number.isInteger(position)) {
      return;
    }

    const isSize = optionName === 'size' || optionName.includes('weight');
    const isRoastDate = optionName.includes('roast date');

    if (!isSize && !isRoastDate) {
      return;
    }

    const currentValues = [...values.children];
    const sortedValues = currentValues
      .map((element, index) => {
        const optionValue = element.querySelector('[data-option-input]')?.value || '';
        const sortValue = isSize
          ? parseWeightInGrams(optionValue)
          : getRoastDateSortValue(variants, position, optionValue);
        return { element, index, sortValue };
      })
      .sort((a, b) => a.sortValue - b.sortValue || a.index - b.index)
      .map(({ element }) => element);

    const groupOrderChanged = sortedValues.some(
      (element, index) => element !== currentValues[index],
    );

    if (groupOrderChanged) {
      values.append(...sortedValues);
      orderChanged = true;
    }
  });

  return orderChanged;
};

const setMedia = (
  root,
  mediaId,
  { updateStage = true, updateDialog = true, notify = true } = {},
) => {
  if (!mediaId) {
    return;
  }

  if (updateStage) {
    root.querySelectorAll('[data-product-media]').forEach((media) => {
      media.hidden = media.dataset.productMedia !== String(mediaId);
    });
  }

  if (updateDialog) {
    root.querySelectorAll('[data-product-dialog-media]').forEach((media) => {
      media.hidden = media.dataset.productDialogMedia !== String(mediaId);
    });
  }

  root.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
    const isActive = thumbnail.dataset.productThumbnail === String(mediaId);
    thumbnail.classList.toggle('is-active', isActive);
    thumbnail.setAttribute('aria-pressed', String(isActive));
  });

  if (notify) {
    root.dispatchEvent(
      new CustomEvent('sigma:product-media-change', {
        detail: { mediaId: String(mediaId) },
      }),
    );
  }
};

const swapVariantState = (element, nextKey, applyState, { animate = true } = {}) => {
  if (!element || typeof applyState !== 'function') {
    return;
  }

  let state = variantSwapStates.get(element);

  if (!state) {
    state = { frameId: 0, sequence: 0, swapTimeoutId: 0, settleTimeoutId: 0 };
    variantSwapStates.set(element, state);
  }

  state.sequence += 1;
  const sequence = state.sequence;

  window.clearTimeout(state.swapTimeoutId);
  window.clearTimeout(state.settleTimeoutId);
  window.cancelAnimationFrame(state.frameId);

  if (element.dataset.variantSwapKey === nextKey) {
    element.classList.remove('is-exit', 'is-enter-start', 'is-swapping');
    return;
  }

  if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    element.classList.remove('is-exit', 'is-enter-start', 'is-swapping');
    applyState();
    element.dataset.variantSwapKey = nextKey;
    return;
  }

  const wasHidden = element.hidden;

  if (wasHidden) {
    const shouldEnter = applyState() !== false;
    element.dataset.variantSwapKey = nextKey;

    if (!shouldEnter) {
      return;
    }

    element.classList.add('is-swapping', 'is-enter-start');
    element.getBoundingClientRect();
    state.frameId = window.requestAnimationFrame(() => {
      element.classList.remove('is-enter-start');
      state.settleTimeoutId = window.setTimeout(() => {
        if (state.sequence === sequence) {
          element.classList.remove('is-swapping');
        }
      }, VARIANT_SWAP_TIMING.phase);
    });
    return;
  }

  element.classList.remove('is-enter-start');
  element.classList.add('is-swapping', 'is-exit');

  state.swapTimeoutId = window.setTimeout(() => {
    if (state.sequence !== sequence) {
      return;
    }

    const shouldEnter = applyState() !== false;
    element.dataset.variantSwapKey = nextKey;
    element.classList.remove('is-exit');

    if (!shouldEnter) {
      element.classList.remove('is-swapping');
      return;
    }

    element.classList.add('is-enter-start');
    element.getBoundingClientRect();

    state.frameId = window.requestAnimationFrame(() => {
      if (state.sequence === sequence) {
        element.classList.remove('is-enter-start');
        state.settleTimeoutId = window.setTimeout(() => {
          if (state.sequence === sequence) {
            element.classList.remove('is-swapping');
          }
        }, VARIANT_SWAP_TIMING.phase);
      }
    });
  }, VARIANT_SWAP_TIMING.phase);
};

const updateSwappingText = (element, nextText, { animate = true } = {}) => {
  if (!element || !nextText) {
    return;
  }

  swapVariantState(
    element,
    `text:${nextText}`,
    () => {
      element.textContent = nextText;
      return true;
    },
    { animate },
  );
};

const updateBundlePurchaseLabel = (label, selectedCount, { animate = true } = {}) => {
  if (!label || label.closest('button')?.dataset.availability !== 'available') {
    return;
  }

  const normalizedSelectedCount = Math.max(0, Number(selectedCount) || 0);
  const totalSelectedItems = normalizedSelectedCount + 1;
  const nextText = normalizedSelectedCount === 0
    ? 'Add to bag'
    : normalizedSelectedCount === 1
      ? 'Add both to bag'
      : `Add ${totalSelectedItems} items to bag`;
  swapBundlePurchaseLabel(label, nextText, {
    animate,
    rank: normalizedSelectedCount,
  });
};

const getVariantStockState = (variant) => {
  const inventoryQuantity = Number(variant?.inventoryQuantity);
  const managesInventory = Boolean(variant?.inventoryManagement);
  const isLowStock =
    variant?.available &&
    managesInventory &&
    Number.isInteger(inventoryQuantity) &&
    inventoryQuantity > 0 &&
    inventoryQuantity <= 3;

  if (!variant?.available) {
    return { key: 'unavailable', label: 'Sold out', isLowStock: false };
  }

  if (isLowStock) {
    return {
      key: `low-stock:${inventoryQuantity}`,
      label: `Only ${inventoryQuantity} left!`,
      isLowStock: true,
    };
  }

  return { key: 'in-stock', label: '', isLowStock: false };
};

const setStockNote = (stockNote, variant, { animate = true } = {}) => {
  const stockState = getVariantStockState(variant);

  swapVariantState(
    stockNote,
    `stock:${stockState.key}`,
    () => {
      stockNote.classList.remove('is-low-stock', 'is-in-stock');

      if (!variant?.available || !stockState.isLowStock) {
        stockNote.hidden = true;
        stockNote.textContent = '';
        return false;
      }

      stockNote.classList.add('is-low-stock');
      stockNote.textContent = stockState.label;
      stockNote.hidden = false;
      return true;
    },
    { animate },
  );
};

const getRestGuidance = (variant) => {
  if (variant?.restGuidanceValid !== true) return null;

  const maxWeeks = variant.recommendedRestWeeks;
  const hasMinWeeks = Object.prototype.hasOwnProperty.call(variant, 'recommendedRestMinWeeks');
  const hasSource = Object.prototype.hasOwnProperty.call(variant, 'restGuidanceSource');
  let minWeeks;
  let source;

  if (!hasMinWeeks && !hasSource) {
    minWeeks = maxWeeks;
    source = 'roaster';
  } else if (hasMinWeeks && hasSource) {
    minWeeks = variant.recommendedRestMinWeeks;
    source = variant.restGuidanceSource;
  } else {
    return null;
  }

  if (
    !Number.isInteger(minWeeks)
    || !Number.isInteger(maxWeeks)
    || minWeeks < 1
    || maxWeeks > 16
    || minWeeks > maxWeeks
    || !['sigma', 'roaster'].includes(source)
  ) {
    return null;
  }

  return { minWeeks, maxWeeks, source };
};

const setReadyNowTags = (readyNowTags, variant, { animate = true } = {}) => {
  const isReadyNow = Boolean(variant?.available && variant?.readyNow === true);
  const guidanceValid = Boolean(getRestGuidance(variant));
  const publicBonus = readyNowTags?.querySelector('[data-rested-public-offer="true"]');
  const status = readyNowTags?.querySelector('[data-rest-guidance-status]');
  if (publicBonus) {
    setRestedBonusElementVariant(publicBonus, variant);
  }
  if (status) {
    status.textContent = isReadyNow
      ? guidanceValid
        ? 'Ready to brew'
        : 'Rested and ready to brew'
      : '';
  }

  swapVariantState(
    readyNowTags,
    `ready-now:${isReadyNow}`,
    () => {
      readyNowTags.hidden = !isReadyNow;
      return isReadyNow;
    },
    { animate },
  );
};

const setRestGuidanceExplanation = (row, explanation, variant) => {
  const guidance = getRestGuidance(variant);
  const guidanceValid = Boolean(guidance);

  if (row) {
    row.hidden = !guidanceValid;
  }
  const duration = row?.querySelector('[data-rest-guidance-duration]');
  if (duration) {
    if (!guidance) {
      duration.textContent = '';
    } else if (guidance.minWeeks !== guidance.maxWeeks) {
      duration.textContent = `Recommended rest: ${guidance.minWeeks}–${guidance.maxWeeks} weeks from roast.`;
    } else {
      const unit = guidance.maxWeeks === 1 ? 'week' : 'weeks';
      duration.textContent = `Recommended rest: ${guidance.maxWeeks} ${unit} from roast.`;
    }
  }
  if (explanation) {
    const isReadyNow = variant?.available && variant?.readyNow === true;
    explanation.textContent = !guidance ? '' : guidance.source === 'sigma'
      ? isReadyNow
        ? 'Rested in line with Sigma’s recommendation. Ready to brew.'
        : 'Based on Sigma’s recommendation.'
      : isReadyNow
        ? "Rested in line with the roaster's recommendation. Ready to brew."
        : "Based on the roaster's recommendation.";
  }
};

const getProductQuantity = (root) => {
  const quantity = Number.parseInt(root.querySelector('[data-quantity-input]')?.value, 10);
  return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
};

const formatVariantTotal = (root, variant, quantity) => {
  return formatProductQuantityTotal(variant, quantity, root.dataset.moneyReference);
};

const updateAddButtonTotal = (root, variant, quantity, { animate = true } = {}) => {
  const addButtonPrices = root.querySelectorAll(
    '[data-add-to-bag-price], [data-sticky-add-to-bag-price]',
  );

  if (!variant || !addButtonPrices.length) {
    return;
  }

  addButtonPrices.forEach((addButtonPrice) => {
    updateSwappingText(addButtonPrice, formatVariantTotal(root, variant, quantity), { animate });
  });
};

const updateStickyPurchaseQuantity = (root, quantity, { animate = true } = {}) => {
  const stickyQuantity = root.querySelector('[data-sticky-selected-quantity-value]');

  updateSwappingText(stickyQuantity, String(quantity), { animate });
};

const updateOptionAvailability = (root, variants) => {
  const currentOptions = selectedOptions(root);
  const optionGroups = [...root.querySelectorAll('[data-option-position]')];
  const includeSoldOutVariants = Boolean(FELLOW_BREADCRUMB_PATHS[root.dataset.fellowProduct]);
  const privateHold = root.hasAttribute('data-private-mystery-hold');

  optionGroups.forEach((group, groupIndex) => {
    const position = Number(group?.dataset.optionPosition);

    if (!Number.isInteger(position)) {
      return;
    }

    group.querySelectorAll('[data-option-input]').forEach((input) => {
      const isFinishInput = Boolean(input.closest('.coffee-product__option-value--finish-swatch'));
      input.disabled = !variants.some(
        (variant) =>
          (privateHold || includeSoldOutVariants || isFinishInput || variant.available) &&
          variant.options[position] === input.value &&
          optionGroups.slice(0, groupIndex).every((previousGroup) => {
            const previousPosition = Number(previousGroup.dataset.optionPosition);
            return (
              Number.isInteger(previousPosition) &&
              variant.options[previousPosition] === currentOptions[previousPosition]
            );
          }),
      );
      if (isFinishInput) {
        const available = variants.some((variant) => (privateHold || variant.available)
          && variant.options[position] === input.value
          && optionGroups.slice(0, groupIndex).every((previousGroup) =>
            variant.options[Number(previousGroup.dataset.optionPosition)]
              === currentOptions[Number(previousGroup.dataset.optionPosition)]));
        input.dataset.finishUnavailable = String(!available);
        const name = available ? input.value : `${input.value}, ${input.disabled ? 'unavailable with the selected options' : 'sold out'}`;
        input.setAttribute('aria-label', name);
      }

    });
  });
};

const normalizeOptionSelection = (root, variants) => {
  updateOptionAvailability(root, variants);
  let selectionChanged = false;

  root.querySelectorAll('[data-option-position]').forEach((group) => {
    const selectedInput = group.querySelector('[data-option-input]:checked');

    if (selectedInput && !selectedInput.disabled) {
      return;
    }

    const nextInput = [...group.querySelectorAll('[data-option-input]')].find(
      (input) => !input.disabled,
    );

    if (nextInput) {
      nextInput.checked = true;
      selectionChanged = true;
      updateOptionAvailability(root, variants);
    }
  });

  if (selectionChanged) {
    updateOptionAvailability(root, variants);
  }
};

const syncLibraryCardSellingPlan = (root, variant) => {
  if (!root.hasAttribute('data-library-card-product')) {
    return true;
  }

  const planInput = root.querySelector('[data-library-card-selling-plan]');
  const planId = String(variant?.libraryCardSellingPlanId || '');
  const sellingPlanAvailable = Boolean(planInput && planId);

  if (planInput) {
    planInput.value = planId;
  }

  root.querySelectorAll('[data-library-card-plan-error]').forEach((error) => {
    error.hidden = sellingPlanAvailable;
  });

  root.querySelectorAll('[data-library-card-charge-summary]').forEach((summary) => {
    summary.hidden = !sellingPlanAvailable;
  });

  if (sellingPlanAvailable) {
    const chargeValues = new Map([
      ['[data-library-card-credit-value]', variant.price],
      ['[data-library-card-first-charge]', variant.libraryCardFirstCharge],
      ['[data-library-card-ongoing-charge]', variant.libraryCardOngoingCharge],
      [
        '[data-library-card-intro-count]',
        String(variant.libraryCardIntroductoryPaymentCount ?? ''),
      ],
      [
        '[data-library-card-ongoing-number]',
        String(Number(variant.libraryCardIntroductoryPaymentCount) + 1),
      ],
    ]);

    chargeValues.forEach((value, selector) => {
      if (!value) return;
      root.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    });
  }

  return sellingPlanAvailable;
};

const getAvailablePurchaseLabel = (root) =>
  root.hasAttribute('data-library-card-product') ? 'Add monthly top-up to bag' : 'Add to bag';

const syncStickyPurchaseState = (root, variant, { animate = true } = {}) => {
  const panel = root.querySelector('[data-sticky-purchase]');

  if (!panel) {
    return;
  }

  const stickyPrice = panel.querySelector('[data-sticky-purchase-price]');
  const stickyAddButton = panel.querySelector('[data-sticky-add-to-bag]');
  const stickyAddButtonText = panel.querySelector('[data-sticky-add-to-bag-text]');
  const stickyAddButtonPriceGroup = panel.querySelector(
    '[data-sticky-add-to-bag-price-group]',
  );
  const stickyStockNote = panel.querySelector('[data-sticky-stock-note]');
  const currentOptions = selectedOptions(root);

  updateStickyPurchaseQuantity(root, getProductQuantity(root), { animate });

  panel.querySelectorAll('[data-sticky-selected-option]').forEach((option) => {
    const position = Number(option.dataset.stickySelectedOptionPosition);
    const value = option.querySelector('[data-sticky-selected-option-value]');
    const selectedValue = currentOptions[position];

    updateSwappingText(value, selectedValue, { animate });
  });

  if (!variant) {
    stickyAddButton.disabled = true;
    stickyAddButton.dataset.availability = 'unavailable';
    stickyAddButtonText.textContent = 'Unavailable';
    stickyAddButtonPriceGroup.hidden = true;
    stickyStockNote.hidden = true;
    stickyStockNote.textContent = '';
    stickyStockNote.classList.remove('is-low-stock', 'is-in-stock');
    return;
  }

  updateSwappingText(stickyPrice, variant.price, { animate });
  if (root.hasAttribute('data-private-mystery-hold')) {
    stickyAddButton.disabled = true;
    stickyAddButton.dataset.availability = 'checking';
    stickyAddButtonText.textContent = 'Checking availability…';
    stickyAddButtonPriceGroup.hidden = false;
    stickyStockNote.hidden = true;
    stickyStockNote.textContent = '';
    stickyStockNote.classList.remove('is-low-stock', 'is-in-stock');
    return;
  }
  const sellingPlanAvailable = syncLibraryCardSellingPlan(root, variant);
  stickyAddButton.disabled = !variant.available || !sellingPlanAvailable;
  stickyAddButton.dataset.availability = !sellingPlanAvailable
    ? 'unavailable'
    : variant.available ? 'available' : 'sold-out';
  stickyAddButtonText.textContent = !sellingPlanAvailable
    ? 'Unavailable'
    : variant.available ? getAvailablePurchaseLabel(root) : 'Sold out';
  stickyAddButtonPriceGroup.hidden = false;
  setStockNote(stickyStockNote, variant, { animate });
};

const updateProductState = (
  root,
  variants,
  { animatePrice = true, updateUrl = true, preserveSelection = false } = {},
) => {
  const variantSelect = root.querySelector('[data-variant-select]');
  const price = root.querySelector('[data-product-price]');
  const addButton = root.querySelector('[data-add-to-bag]');
  const addButtonText = root.querySelector('[data-add-to-bag-text]');
  const addButtonPriceGroup = root.querySelector('[data-add-to-bag-price-group]');
  const stockNote = root.querySelector('[data-stock-note]');
  const readyNowTags = root.querySelector('[data-ready-now-tags]');
  const restGuidanceRow = root.querySelector('[data-rest-guidance-row]');
  const restGuidanceExplanation = root.querySelector('[data-rest-guidance-explanation]');
  const variantStatus = root.querySelector('[data-product-variant-status]');
  const goldCover = root.querySelector('[data-gold-cover][data-gold-cover-variant-id]');
  const goldCoverButton = goldCover?.querySelector('[data-gold-cover-add]');
  const privateHold = root.hasAttribute('data-private-mystery-hold');

  if (!preserveSelection) {
    normalizeOptionSelection(root, variants);
  }

  const variant = resolveSelectedVariant(root, variants);
  syncFinishSelectedValues(root, variant);
  setRestedBonusVariant(root, variant);

  if (!variant) {
    syncLibraryCardSellingPlan(root, null);
    addButton.disabled = true;
    addButton.dataset.availability = 'unavailable';
    if (addButtonText) addButtonText.textContent = 'Unavailable';
    if (addButtonPriceGroup) addButtonPriceGroup.hidden = true;
    stockNote.hidden = true;
    stockNote.textContent = '';
    stockNote.classList.remove('is-low-stock', 'is-in-stock');
    setReadyNowTags(readyNowTags, null, { animate: animatePrice });
    if (restGuidanceRow || restGuidanceExplanation) {
      setRestGuidanceExplanation(restGuidanceRow, restGuidanceExplanation, null);
    }
    if (goldCoverButton) {
      if (!goldCoverButton.disabled) goldCoverButton.disabled = true;
      const hideGoldCoverButton = goldCover.dataset.goldCoverModalReady !== 'true';
      if (goldCoverButton.hidden !== hideGoldCoverButton) {
        goldCoverButton.hidden = hideGoldCoverButton;
      }
    }
    if (animatePrice && variantStatus) {
      variantStatus.textContent = 'Unavailable';
    }
    syncStickyPurchaseState(root, null, { animate: animatePrice });
    return;
  }

  variantSelect.value = String(variant.id);
  const sellingPlanAvailable = syncLibraryCardSellingPlan(root, variant);
  updateSwappingText(price, variant.price, { animate: animatePrice });
  addButton.disabled = privateHold || !variant.available || !sellingPlanAvailable;
  addButton.dataset.availability = privateHold
    ? 'checking'
    : !sellingPlanAvailable ? 'unavailable' : variant.available ? 'available' : 'sold-out';
  if (addButtonText) {
    addButtonText.textContent = privateHold
      ? 'Checking availability…'
      : !sellingPlanAvailable
        ? 'Unavailable'
        : variant.available ? getAvailablePurchaseLabel(root) : 'Sold out';
  }
  if (addButtonPriceGroup) addButtonPriceGroup.hidden = false;
  updateAddButtonTotal(root, variant, getProductQuantity(root), { animate: animatePrice });
  if (privateHold) {
    stockNote.hidden = true;
    stockNote.textContent = '';
    stockNote.classList.remove('is-low-stock', 'is-in-stock');
    setReadyNowTags(readyNowTags, null, { animate: animatePrice });
  } else {
    setStockNote(stockNote, variant, { animate: animatePrice });
    setReadyNowTags(readyNowTags, variant, { animate: animatePrice });
  }
  if (restGuidanceRow || restGuidanceExplanation) {
    setRestGuidanceExplanation(
      restGuidanceRow,
      restGuidanceExplanation,
      privateHold ? null : variant,
    );
  }
  if (goldCoverButton) {
    const disableGoldCoverButton =
      !variant.available || goldCover.dataset.goldCoverAvailable !== 'true';
    if (goldCoverButton.disabled !== disableGoldCoverButton) {
      goldCoverButton.disabled = disableGoldCoverButton;
    }
    const hideGoldCoverButton = goldCover.dataset.goldCoverModalReady !== 'true';
    if (goldCoverButton.hidden !== hideGoldCoverButton) {
      goldCoverButton.hidden = hideGoldCoverButton;
    }
  }
  syncStickyPurchaseState(root, variant, { animate: animatePrice });
  if (animatePrice && variantStatus) {
    const stockLabel = privateHold ? 'Checking availability' : getVariantStockState(variant).label;
    const variantStatusParts = [
      variant.price,
      !privateHold && variant.available && variant.readyNow === true
        ? Boolean(getRestGuidance(variant))
          ? 'Ready to brew'
          : 'Rested and ready to brew'
        : '',
      stockLabel,
    ].filter(Boolean);
    variantStatus.textContent = variantStatusParts.join(', ');
  }
  setMedia(root, variant.featuredMediaId);
  const colourGallery = root.querySelector('[data-product-gallery]');
  colourGallery?.toggleAttribute('data-colour-image-missing', variant.colourImageMissing === true);
  const colourNotice = root.querySelector('[data-colour-image-notice]');
  if (colourNotice) colourNotice.hidden = variant.colourImageMissing !== true;

  if (updateUrl && window.history?.replaceState) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
};

export const initializeQuantityPicker = (root, onQuantityChange) => {
  const quantityPicker = root.querySelector('[data-quantity-picker]');
  const quantityInput = root.querySelector('[data-quantity-input]');
  const quantityVisual = root.querySelector('[data-quantity-visual]');
  const decrease = root.querySelector('[data-quantity-decrease]');
  const increase = root.querySelector('[data-quantity-increase]');

  if (!quantityPicker || !quantityInput || !quantityVisual || !decrease || !increase) {
    return;
  }

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let displayedQuantity = Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);
  const quantityTicker = createNumberTicker(quantityVisual, {
    initialValue: String(displayedQuantity),
    reducedMotion: motionPreference,
  });

  const getMinimum = () => Math.max(1, Number(quantityInput.min) || 1);
  const getMaximum = () => {
    const maximum = Number.parseInt(quantityInput.max, 10);
    return Number.isInteger(maximum) ? maximum : Number.POSITIVE_INFINITY;
  };
  const getStep = () => Math.max(1, Number(quantityInput.step) || 1);

  const clampQuantity = (value) =>
    Math.min(getMaximum(), Math.max(getMinimum(), Number.isInteger(value) ? value : getMinimum()));

  const notifyQuantityChange = (value, { animate = true } = {}) => {
    if (typeof onQuantityChange === 'function') {
      onQuantityChange(value, { animate });
    }
  };

  const syncButtonState = (value) => {
    decrease.disabled = value <= getMinimum();
    increase.disabled = value >= getMaximum();
  };

  const renderQuantity = (value) => {
    quantityTicker.set(String(value), { animate: false, numericValue: value });
    displayedQuantity = value;
  };

  const rollQuantity = (value, direction, duration = QUANTITY_STEPPER_TIMING.digitRoll) => {
    if (value === displayedQuantity) {
      renderQuantity(value);
      return;
    }

    quantityTicker.set(String(value), { direction, duration, numericValue: value });
    displayedQuantity = value;
  };

  const normalizeQuantity = ({ animate = true } = {}) => {
    const value = clampQuantity(Number.parseInt(quantityInput.value, 10));
    quantityInput.value = String(value);
    syncButtonState(value);
    renderQuantity(value);
    notifyQuantityChange(value, { animate });
    return value;
  };

  const stepQuantity = (direction, duration = QUANTITY_STEPPER_TIMING.digitRoll) => {
    const current = clampQuantity(Number.parseInt(quantityInput.value, 10));
    const next = clampQuantity(current + direction * getStep());

    if (next === current) {
      syncButtonState(current);
      return false;
    }

    quantityInput.value = String(next);
    syncButtonState(next);
    rollQuantity(next, direction, duration);
    notifyQuantityChange(next);
    return true;
  };

  const attachPressAndHold = (button, direction) => {
    let holdTimeoutId;
    let repeatTimeoutId;
    let pointerId;
    let suppressClick = false;
    let repeatStartedAt = 0;

    const setHoldStage = (stage) => {
      button.dataset.quantityHoldStage = String(stage);
    };

    const clearHoldTimers = () => {
      window.clearTimeout(holdTimeoutId);
      window.clearTimeout(repeatTimeoutId);
      holdTimeoutId = undefined;
      repeatTimeoutId = undefined;
    };

    const stopHold = ({ cancelClick = false } = {}) => {
      clearHoldTimers();
      setHoldStage(QUANTITY_HOLD_STAGE.idle);

      if (pointerId !== undefined && button.hasPointerCapture?.(pointerId)) {
        button.releasePointerCapture(pointerId);
      }

      pointerId = undefined;

      if (cancelClick) {
        suppressClick = false;
      }
    };

    const repeatStep = () => {
      const elapsed = performance.now() - repeatStartedAt;
      const progress = Math.min(
        1,
        elapsed / QUANTITY_STEPPER_TIMING.repeatAccelerationDuration,
      );
      const interval =
        QUANTITY_STEPPER_TIMING.repeatStartInterval -
        (QUANTITY_STEPPER_TIMING.repeatStartInterval -
          QUANTITY_STEPPER_TIMING.repeatMinInterval) *
          progress;
      const rollDuration = Math.max(
        QUANTITY_STEPPER_TIMING.repeatRollFloor,
        interval * QUANTITY_STEPPER_TIMING.repeatedDurationRatio,
      );

      if (!stepQuantity(direction, rollDuration)) {
        stopHold({ cancelClick: true });
        return;
      }

      repeatTimeoutId = window.setTimeout(repeatStep, interval);
    };

    const beginRepeat = () => {
      setHoldStage(QUANTITY_HOLD_STAGE.repeating);
      repeatStartedAt = performance.now();
      repeatStep();
    };

    button.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || button.disabled) {
        return;
      }

      pointerId = event.pointerId;
      suppressClick = true;

      try {
        button.setPointerCapture?.(pointerId);
      } catch {
        // Pointer capture can be unavailable for synthetic or legacy pointer events.
      }

      setHoldStage(QUANTITY_HOLD_STAGE.pressed);
      stepQuantity(direction);
      holdTimeoutId = window.setTimeout(beginRepeat, QUANTITY_STEPPER_TIMING.holdDelay);
    });

    button.addEventListener('pointerup', () => stopHold());

    button.addEventListener('pointercancel', () => stopHold({ cancelClick: true }));
    button.addEventListener('lostpointercapture', () => stopHold());
    button.addEventListener('click', (event) => {
      if (suppressClick && event.detail !== 0) {
        event.preventDefault();
        suppressClick = false;
        return;
      }

      suppressClick = false;
      stepQuantity(direction);
    });

    window.addEventListener('blur', () => stopHold({ cancelClick: true }));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopHold({ cancelClick: true });
      }
    });
  };

  quantityInput.addEventListener('input', () => {
    const value = Number.parseInt(quantityInput.value, 10);

    if (!Number.isInteger(value)) {
      return;
    }

    const clampedValue = clampQuantity(value);
    syncButtonState(clampedValue);
    renderQuantity(clampedValue);
    notifyQuantityChange(clampedValue);
  });
  quantityInput.addEventListener('blur', normalizeQuantity);

  normalizeQuantity({ animate: false });
  quantityPicker.classList.add('is-animated');
  attachPressAndHold(decrease, -1);
  attachPressAndHold(increase, 1);

  return {
    reset({ notify = true } = {}) {
      const current = clampQuantity(Number.parseInt(quantityInput.value, 10));
      const resetValue = clampQuantity(1);

      if (current === resetValue) {
        syncButtonState(current);
        return false;
      }

      quantityInput.value = String(resetValue);
      syncButtonState(resetValue);
      rollQuantity(resetValue, -1);

      if (notify) {
        notifyQuantityChange(resetValue);
      }

      return true;
    },
  };
};

const initializeGallery = (root) => {
  const stage = root.querySelector('[data-product-media-stage]');
  const thumbnails = [...root.querySelectorAll('[data-product-thumbnail]')];
  const thumbnailRail = root.querySelector('.coffee-product__thumbnails');
  const stageMedia = [...root.querySelectorAll('[data-product-media]')];

  if (!stage || thumbnails.length < 2) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopGallery = window.matchMedia('(min-width: 840px)');
  const touchGallery = window.matchMedia('(pointer: coarse)');
  let activePointerId = null;
  let gesture = null;
  let runningTransition = null;
  let transitionSequence = 0;
  let suppressNextClick = false;

  const clearMediaMotion = (media) => {
    media?.style.removeProperty('opacity');
    media?.style.removeProperty('transform');
    media?.style.removeProperty('z-index');
  };

  const getActiveIndex = () => stageMedia.findIndex((media) => !media.hidden);

  const preloadAdjacentImages = () => {
    if (desktopGallery.matches && !touchGallery.matches) return;

    const activeIndex = getActiveIndex();
    if (activeIndex < 0) return;

    // Warm only the two possible swipe destinations, using their responsive sources.
    [-1, 1].forEach((direction) => {
      const index = activeIndex + direction;
      const image = stageMedia[index]?.querySelector('img');
      if (!image) return;

      if (image.fetchPriority !== 'high') image.fetchPriority = 'low';
      image.loading = 'eager';
      image.decode?.().catch(() => {});
    });
  };

  const settleStage = (targetIndex, { notify = true } = {}) => {
    const targetMedia = stageMedia[targetIndex];

    if (!targetMedia) {
      return;
    }

    stageMedia.forEach((media, index) => {
      clearMediaMotion(media);
      media.hidden = index !== targetIndex;
      if (index === targetIndex) {
        media.removeAttribute('aria-hidden');
      } else {
        media.setAttribute('aria-hidden', 'true');
      }
    });
    stage.removeAttribute('data-gallery-moving');
    stage.removeAttribute('data-gallery-dragging');
    setMedia(root, targetMedia.dataset.productMedia, { notify });
  };

  const cancelRunningTransition = ({ settle = true } = {}) => {
    if (!runningTransition) {
      return;
    }

    const transition = runningTransition;
    runningTransition = null;
    transitionSequence += 1;
    transition.animations.forEach((animation) => animation.cancel());

    if (settle) {
      settleStage(transition.targetIndex, { notify: transition.notify });
    } else {
      stageMedia.forEach(clearMediaMotion);
      stage.removeAttribute('data-gallery-moving');
    }
  };

  const scrollThumbnailIntoView = (targetIndex) => {
    thumbnails[targetIndex]?.scrollIntoView({
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  const runTransition = ({ animations, targetIndex, notify = true }) => {
    const sequence = ++transitionSequence;
    runningTransition = { animations, notify, sequence, targetIndex };

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      if (!runningTransition || runningTransition.sequence !== sequence) {
        return;
      }

      runningTransition = null;
      animations.forEach((animation) => animation.cancel());
      settleStage(targetIndex, { notify });
    });
  };

  const prepareMediaPair = (fromIndex, targetIndex) => {
    stageMedia.forEach((media, index) => {
      clearMediaMotion(media);
      media.hidden = index !== fromIndex && index !== targetIndex;
      media.setAttribute('aria-hidden', 'true');
    });
    stageMedia[targetIndex]?.removeAttribute('aria-hidden');
  };

  const animateToIndex = (
    targetIndex,
    direction,
    { outgoingStart = 0, incomingStart, duration = GALLERY_SWIPE.settleDuration } = {},
  ) => {
    cancelRunningTransition();

    const fromIndex = getActiveIndex();
    const outgoing = stageMedia[fromIndex];
    const incoming = stageMedia[targetIndex];

    if (!outgoing || !incoming || fromIndex === targetIndex) {
      settleStage(targetIndex);
      return;
    }

    prepareMediaPair(fromIndex, targetIndex);
    setMedia(root, incoming.dataset.productMedia, { updateStage: false, notify: false });
    scrollThumbnailIntoView(targetIndex);
    stage.setAttribute('data-gallery-moving', 'true');

    if (typeof outgoing.animate !== 'function' || typeof incoming.animate !== 'function') {
      settleStage(targetIndex);
      return;
    }

    if (reducedMotion.matches) {
      outgoing.style.zIndex = '1';
      incoming.style.zIndex = '2';
      runTransition({
        targetIndex,
        animations: [
          outgoing.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: GALLERY_SWIPE.reducedMotionDuration,
            easing: 'ease-out',
            fill: 'both',
          }),
          incoming.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: GALLERY_SWIPE.reducedMotionDuration,
            easing: 'ease-out',
            fill: 'both',
          }),
        ],
      });
      return;
    }

    const distance = stage.getBoundingClientRect().width;
    const resolvedIncomingStart = incomingStart ?? direction * distance;
    const animations = [
      outgoing.animate(
        [
          { transform: `translate3d(${outgoingStart}px, 0, 0)` },
          { transform: `translate3d(${-direction * distance}px, 0, 0)` },
        ],
        { duration, easing: GALLERY_SWIPE.easing, fill: 'both' },
      ),
      incoming.animate(
        [
          { transform: `translate3d(${resolvedIncomingStart}px, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ],
        { duration, easing: GALLERY_SWIPE.easing, fill: 'both' },
      ),
    ];

    runTransition({ animations, targetIndex });
  };

  const returnGestureToRest = (currentGesture) => {
    const outgoing = stageMedia[currentGesture.activeIndex];
    const incoming = stageMedia[currentGesture.targetIndex];

    if (!outgoing || reducedMotion.matches || typeof outgoing.animate !== 'function') {
      settleStage(currentGesture.activeIndex, { notify: false });
      return;
    }

    stage.setAttribute('data-gallery-moving', 'true');
    const animations = [
      outgoing.animate(
        [
          { transform: `translate3d(${currentGesture.renderedX}px, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ],
        {
          duration: GALLERY_SWIPE.returnDuration,
          easing: GALLERY_SWIPE.easing,
          fill: 'both',
        },
      ),
    ];

    if (incoming && typeof incoming.animate === 'function') {
      const distance = stage.getBoundingClientRect().width;
      animations.push(
        incoming.animate(
          [
            {
              transform: `translate3d(${currentGesture.direction * distance + currentGesture.renderedX}px, 0, 0)`,
            },
            { transform: `translate3d(${currentGesture.direction * distance}px, 0, 0)` },
          ],
          {
            duration: GALLERY_SWIPE.returnDuration,
            easing: GALLERY_SWIPE.easing,
            fill: 'both',
          },
        ),
      );
    }

    runTransition({
      animations,
      targetIndex: currentGesture.activeIndex,
      notify: false,
    });
  };

  const updateThumbnailRailHeight = () => {
    if (!thumbnailRail) {
      return;
    }

    if (!desktopGallery.matches) {
      thumbnailRail.style.removeProperty('--product-thumbnail-rail-height');
      return;
    }

    const thumbnail = thumbnails[0];
    const thumbnailHeight = thumbnail.getBoundingClientRect().height;
    const stageHeight = stage.getBoundingClientRect().height;
    const railStyles = window.getComputedStyle(thumbnailRail);
    const gap = Number.parseFloat(railStyles.rowGap || railStyles.gap) || 0;

    if (!thumbnailHeight || !stageHeight) {
      return;
    }

    const visibleCount = Math.max(
      1,
      Math.floor((stageHeight + gap) / (thumbnailHeight + gap)),
    );
    const railHeight = Math.min(
      stageHeight,
      visibleCount * thumbnailHeight + Math.max(0, visibleCount - 1) * gap,
    );
    thumbnailRail.style.setProperty(
      '--product-thumbnail-rail-height',
      `${Math.floor(railHeight)}px`,
    );
  };

  thumbnails.forEach((thumbnail, targetIndex) => {
    thumbnail.addEventListener('click', () => {
      cancelRunningTransition();
      const activeIndex = getActiveIndex();

      if (activeIndex === targetIndex) {
        return;
      }

      animateToIndex(targetIndex, targetIndex > activeIndex ? 1 : -1);
    });
  });

  updateThumbnailRailHeight();
  desktopGallery.addEventListener?.('change', updateThumbnailRailHeight);
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(updateThumbnailRailHeight).observe(stage);
  } else {
    window.addEventListener('resize', updateThumbnailRailHeight, { passive: true });
  }

  stage.addEventListener('pointerdown', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (
      event.button !== 0 ||
      (event.pointerType !== 'touch' && event.pointerType !== 'pen') ||
      target?.closest('button')
    ) {
      return;
    }

    activePointerId = event.pointerId;
    gesture = {
      activeIndex: getActiveIndex(),
      direction: 0,
      dragging: false,
      renderedX: 0,
      startTime: event.timeStamp,
      startX: event.clientX,
      startY: event.clientY,
      targetIndex: -1,
      verticalIntent: false,
    };

    try {
      stage.setPointerCapture?.(activePointerId);
    } catch {
      // Pointer capture may be unavailable for synthetic or legacy pointer events.
    }
  });

  stage.addEventListener('pointermove', (event) => {
    if (event.pointerId !== activePointerId || !gesture) {
      return;
    }

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const horizontalDistance = Math.abs(deltaX);
    const verticalDistance = Math.abs(deltaY);

    if (!gesture.dragging) {
      if (verticalDistance > horizontalDistance && verticalDistance > 8) {
        gesture.verticalIntent = true;
        return;
      }

      if (
        horizontalDistance < 8 ||
        horizontalDistance <= verticalDistance * GALLERY_SWIPE.directionRatio
      ) {
        return;
      }

      gesture.dragging = true;
    }

    if (gesture.verticalIntent) {
      return;
    }

    const direction = deltaX < 0 ? 1 : -1;
    const targetIndex = gesture.activeIndex + direction;
    const incoming = stageMedia[targetIndex];
    const outgoing = stageMedia[gesture.activeIndex];
    const distance = stage.getBoundingClientRect().width;
    const renderedX = incoming ? deltaX : deltaX * GALLERY_SWIPE.edgeResistance;

    gesture.direction = direction;
    gesture.renderedX = renderedX;
    gesture.targetIndex = incoming ? targetIndex : -1;

    if (reducedMotion.matches) {
      return;
    }

    stageMedia.forEach((media, index) => {
      const isVisible = index === gesture.activeIndex || index === targetIndex;
      if (!isVisible) {
        clearMediaMotion(media);
      }
      media.hidden = !isVisible;
      media.setAttribute('aria-hidden', 'true');
    });
    outgoing?.removeAttribute('aria-hidden');
    if (incoming) {
      incoming.removeAttribute('aria-hidden');
      incoming.style.transform = `translate3d(${direction * distance + renderedX}px, 0, 0)`;
    }
    outgoing.style.transform = `translate3d(${renderedX}px, 0, 0)`;
    stage.setAttribute('data-gallery-dragging', 'true');
    event.preventDefault();
  });

  stage.addEventListener('pointerup', (event) => {
    if (event.pointerId !== activePointerId || !gesture) {
      return;
    }

    const completedGesture = gesture;
    const deltaX = event.clientX - completedGesture.startX;
    const deltaY = event.clientY - completedGesture.startY;
    const horizontalDistance = Math.abs(deltaX);
    const elapsed = Math.max(1, event.timeStamp - completedGesture.startTime);
    const horizontalVelocity = horizontalDistance / elapsed;
    const hasHorizontalIntent =
      horizontalDistance > Math.abs(deltaY) * GALLERY_SWIPE.directionRatio;
    const crossedThreshold =
      horizontalDistance >= GALLERY_SWIPE.minimumDistance ||
      (horizontalDistance >= GALLERY_SWIPE.minimumFlingDistance &&
        horizontalVelocity >= GALLERY_SWIPE.velocityThreshold);

    gesture = null;
    activePointerId = null;

    if (
      completedGesture.dragging &&
      hasHorizontalIntent &&
      crossedThreshold &&
      completedGesture.targetIndex >= 0
    ) {
      suppressNextClick = true;
      const distance = stage.getBoundingClientRect().width;
      const remainingRatio = Math.max(
        0,
        Math.min(1, (distance - Math.abs(completedGesture.renderedX)) / distance),
      );
      const duration = Math.max(
        GALLERY_SWIPE.minimumSettleDuration,
        Math.round(GALLERY_SWIPE.settleDuration * remainingRatio),
      );
      animateToIndex(completedGesture.targetIndex, completedGesture.direction, {
        outgoingStart: completedGesture.renderedX,
        incomingStart:
          completedGesture.direction * distance + completedGesture.renderedX,
        duration,
      });
      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    } else if (completedGesture.dragging) {
      suppressNextClick = true;
      returnGestureToRest(completedGesture);
      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    } else {
      settleStage(completedGesture.activeIndex, { notify: false });
    }
  });

  const cancelGesture = () => {
    if (!gesture) {
      return;
    }

    const cancelledGesture = gesture;
    gesture = null;
    activePointerId = null;
    if (cancelledGesture.dragging) {
      returnGestureToRest(cancelledGesture);
    } else {
      settleStage(cancelledGesture.activeIndex, { notify: false });
    }
  };

  stage.addEventListener('pointercancel', cancelGesture);
  stage.addEventListener('lostpointercapture', cancelGesture);
  stage.addEventListener('click', (event) => {
    if (!suppressNextClick) {
      return;
    }

    event.preventDefault();
    suppressNextClick = false;
  });

  root.addEventListener('sigma:product-media-change', (event) => {
    preloadAdjacentImages();
    if (
      runningTransition &&
      event.detail?.mediaId !==
        String(stageMedia[runningTransition.targetIndex]?.dataset.productMedia)
    ) {
      cancelRunningTransition({ settle: false });
      stageMedia.forEach((media) => {
        clearMediaMotion(media);
        media.setAttribute('aria-hidden', media.hidden ? 'true' : 'false');
      });
    }
  });
  desktopGallery.addEventListener('change', preloadAdjacentImages);
  touchGallery.addEventListener('change', preloadAdjacentImages);
  preloadAdjacentImages();
};

const getMediaControlTone = (image, frame, control) => {
  if (!image.complete || !image.naturalWidth || !image.naturalHeight) {
    return null;
  }

  const canvasSize = MEDIA_CONTROL_TONE.sampleCanvasSize;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  canvas.width = canvasSize;
  canvas.height = canvasSize;

  const imageScale = Math.max(canvasSize / image.naturalWidth, canvasSize / image.naturalHeight);
  const renderedWidth = image.naturalWidth * imageScale;
  const renderedHeight = image.naturalHeight * imageScale;
  const renderedX = (canvasSize - renderedWidth) / 2;
  const renderedY = (canvasSize - renderedHeight) / 2;

  try {
    context.drawImage(image, renderedX, renderedY, renderedWidth, renderedHeight);

    const frameRect = frame.getBoundingClientRect();
    const controlRect = control.getBoundingClientRect();

    if (!frameRect.width || !frameRect.height) {
      return null;
    }

    const centreX =
      ((controlRect.left + controlRect.width / 2 - frameRect.left) / frameRect.width) * canvasSize;
    const centreY =
      ((controlRect.top + controlRect.height / 2 - frameRect.top) / frameRect.height) * canvasSize;
    const sampleSize = Math.max(
      MEDIA_CONTROL_TONE.minimumSampleSize,
      Math.round((MEDIA_CONTROL_TONE.visibleLensSize / frameRect.width) * canvasSize),
    );
    const sampleX = Math.max(0, Math.min(canvasSize - sampleSize, Math.round(centreX - sampleSize / 2)));
    const sampleY = Math.max(0, Math.min(canvasSize - sampleSize, Math.round(centreY - sampleSize / 2)));
    const pixels = context.getImageData(sampleX, sampleY, sampleSize, sampleSize).data;
    let luminanceTotal = 0;
    let opaquePixelCount = 0;

    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3] / 255;

      if (alpha < 0.25) {
        continue;
      }

      const red = pixels[index] / 255;
      const green = pixels[index + 1] / 255;
      const blue = pixels[index + 2] / 255;
      luminanceTotal += red * 0.299 + green * 0.587 + blue * 0.114;
      opaquePixelCount += 1;
    }

    if (!opaquePixelCount) {
      return null;
    }

    const averageLuminance = luminanceTotal / opaquePixelCount;
    return averageLuminance < MEDIA_CONTROL_TONE.darkMediaThreshold ? 'dark' : 'light';
  } catch (error) {
    return null;
  }
};

const initializeAdaptiveMediaControls = (root) => {
  const source = root.querySelector('[data-product-media-stage]');
  const card = root.querySelector('[data-product-media-dialog-card]');
  const sourceControl = root.querySelector('[data-product-media-dialog-open]');

  if (!source || !card || !sourceControl) {
    return;
  }

  let scheduledFrame = 0;
  let pendingImage = null;

  const clearTone = () => {
    delete source.dataset.mediaControlTone;
    delete card.dataset.mediaControlTone;
  };

  const updateTone = () => {
    window.cancelAnimationFrame(scheduledFrame);
    scheduledFrame = window.requestAnimationFrame(() => {
      const image = root.querySelector('[data-product-media]:not([hidden]) img');

      if (!image) {
        clearTone();
        return;
      }

      if (!image.complete || !image.naturalWidth) {
        if (pendingImage !== image) {
          pendingImage = image;
          image.addEventListener(
            'load',
            () => {
              pendingImage = null;
              updateTone();
            },
            { once: true },
          );
        }
        return;
      }

      pendingImage = null;
      const tone = getMediaControlTone(image, source, sourceControl);

      if (!tone) {
        clearTone();
        return;
      }

      source.dataset.mediaControlTone = tone;
      card.dataset.mediaControlTone = tone;
    });
  };

  root.addEventListener('sigma:product-media-change', updateTone);
  updateTone();
};

const initializeMediaDialog = (root) => {
  const source = root.querySelector('[data-product-media-stage]');
  const trigger = root.querySelector('[data-product-media-dialog-open]');
  const dialog = root.querySelector('[data-product-media-dialog]');
  const card = root.querySelector('[data-product-media-dialog-card]');
  const backdrop = root.querySelector('[data-product-media-dialog-dismiss]');
  const closeButton = root.querySelector('[data-product-media-dialog-close]');
  const previousButton = root.querySelector('[data-product-media-dialog-previous]');
  const nextButton = root.querySelector('[data-product-media-dialog-next]');
  const navigationButtons = [previousButton, nextButton].filter(Boolean);
  const dialogMedia = [...root.querySelectorAll('[data-product-dialog-media]')];
  const closeIcon = closeButton?.querySelector('.coffee-product__media-control-icon');
  const loader = root.querySelector('[data-product-media-dialog-loader]');
  const loadError = root.querySelector('[data-product-media-dialog-error]');

  if (
    !source ||
    !trigger ||
    !dialog ||
    !card ||
    !backdrop ||
    !closeButton ||
    !closeIcon ||
    !loader ||
    !loadError ||
    typeof dialog.showModal !== 'function' ||
    typeof card.animate !== 'function'
  ) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fullscreenMedia = window.matchMedia('(min-width: 840px)');
  let state = 'closed';
  let sequence = 0;
  let activeTrigger = null;
  let animations = [];
  let mediaLoadSequence = 0;
  let mediaLoadTimer = 0;
  let mediaSwap = null;
  let mediaSwapSequence = 0;

  const cancelAnimations = () => {
    animations.forEach((animation) => animation.cancel());
    animations = [];
  };

  const animateBackdrop = (keyframes, timing) => {
    try {
      return dialog.animate(keyframes, { ...timing, pseudoElement: '::backdrop' });
    } catch {
      // Older engines still get the native, full-viewport backdrop without a fade.
      return backdrop.animate(keyframes, timing);
    }
  };

  const clearMediaLoadState = ({ invalidate = true } = {}) => {
    if (invalidate) {
      mediaLoadSequence += 1;
    }

    window.clearTimeout(mediaLoadTimer);
    mediaLoadTimer = 0;
    card.classList.remove('is-media-loading', 'is-media-error');
    card.removeAttribute('aria-busy');
    loadError.hidden = true;
  };

  const clearDialogMediaMotion = (media) => {
    media?.style.removeProperty('opacity');
    media?.style.removeProperty('transform');
    media?.style.removeProperty('z-index');
  };

  const settleDialogMedia = (targetIndex, { notify = true } = {}) => {
    const targetMedia = dialogMedia[targetIndex];

    if (!targetMedia) {
      return;
    }

    dialogMedia.forEach((media, index) => {
      clearDialogMediaMotion(media);
      media.hidden = index !== targetIndex;
      if (index === targetIndex) {
        media.removeAttribute('aria-hidden');
      } else {
        media.setAttribute('aria-hidden', 'true');
      }
    });
    card.removeAttribute('data-media-swapping');
    setMedia(root, targetMedia.dataset.productDialogMedia, { notify });
  };

  const cancelMediaSwap = ({ settle = true, notify = true } = {}) => {
    if (!mediaSwap) {
      return;
    }

    const swap = mediaSwap;
    mediaSwap = null;
    mediaSwapSequence += 1;
    swap.animations.forEach((animation) => animation.cancel());

    if (settle) {
      settleDialogMedia(swap.targetIndex, { notify });
    } else {
      dialogMedia.forEach(clearDialogMediaMotion);
      card.removeAttribute('data-media-swapping');
    }
  };

  const runMediaSwap = ({ animations: swapAnimations, targetIndex }) => {
    const swapSequence = ++mediaSwapSequence;
    mediaSwap = { animations: swapAnimations, sequence: swapSequence, targetIndex };

    Promise.allSettled(swapAnimations.map((animation) => animation.finished)).then(() => {
      if (!mediaSwap || mediaSwap.sequence !== swapSequence) {
        return;
      }

      mediaSwap = null;
      swapAnimations.forEach((animation) => animation.cancel());
      settleDialogMedia(targetIndex);
    });
  };

  const showAdjacentMedia = (direction) => {
    if (state !== 'open' || dialogMedia.length < 2) {
      return;
    }

    cancelMediaSwap();
    const activeIndex = dialogMedia.findIndex((media) => !media.hidden);
    const targetIndex = (activeIndex + direction + dialogMedia.length) % dialogMedia.length;
    const outgoing = dialogMedia[activeIndex];
    const incoming = dialogMedia[targetIndex];

    if (!outgoing || !incoming || activeIndex === targetIndex) {
      settleDialogMedia(targetIndex);
      return;
    }

    dialogMedia.forEach((media, index) => {
      clearDialogMediaMotion(media);
      media.hidden = index !== activeIndex && index !== targetIndex;
      media.setAttribute('aria-hidden', 'true');
    });
    incoming.removeAttribute('aria-hidden');
    incoming.querySelector('img')?.setAttribute('loading', 'eager');
    setMedia(root, incoming.dataset.productDialogMedia, {
      updateDialog: false,
      notify: false,
    });
    card.setAttribute('data-media-swapping', 'true');

    if (typeof outgoing.animate !== 'function' || typeof incoming.animate !== 'function') {
      settleDialogMedia(targetIndex);
      return;
    }

    if (reducedMotion.matches) {
      outgoing.style.zIndex = '1';
      incoming.style.zIndex = '2';
      runMediaSwap({
        targetIndex,
        animations: [
          outgoing.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: GALLERY_SWIPE.reducedMotionDuration,
            easing: 'ease-out',
            fill: 'both',
          }),
          incoming.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: GALLERY_SWIPE.reducedMotionDuration,
            easing: 'ease-out',
            fill: 'both',
          }),
        ],
      });
      return;
    }

    const distance = card.getBoundingClientRect().width;
    const timing = {
      duration: GALLERY_SWIPE.settleDuration,
      easing: GALLERY_SWIPE.easing,
      fill: 'both',
    };

    runMediaSwap({
      targetIndex,
      animations: [
        outgoing.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(${-direction * distance}px, 0, 0)` },
          ],
          timing,
        ),
        incoming.animate(
          [
            { transform: `translate3d(${direction * distance}px, 0, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          timing,
        ),
      ],
    });
  };

  const prepareDialogMedia = () => {
    clearMediaLoadState();
    const expectedSequence = mediaLoadSequence;
    const activeMedia = root.querySelector('[data-product-dialog-media]:not([hidden])');
    const image = activeMedia?.querySelector('img');

    if (!image) {
      return;
    }

    image.loading = 'eager';

    const finishLoading = () => {
      if (mediaLoadSequence !== expectedSequence) {
        return;
      }

      window.clearTimeout(mediaLoadTimer);
      mediaLoadTimer = 0;
      card.classList.remove('is-media-loading', 'is-media-error');
      card.removeAttribute('aria-busy');
      loadError.hidden = true;
    };

    const finishAfterDecode = () => {
      if (typeof image.decode !== 'function') {
        finishLoading();
        return;
      }

      image.decode().catch(() => {}).then(finishLoading);
    };

    const showLoadError = () => {
      if (mediaLoadSequence !== expectedSequence) {
        return;
      }

      window.clearTimeout(mediaLoadTimer);
      mediaLoadTimer = 0;
      card.classList.remove('is-media-loading');
      card.classList.add('is-media-error');
      card.removeAttribute('aria-busy');
      loadError.hidden = false;
    };

    if (image.complete) {
      if (image.naturalWidth) {
        finishAfterDecode();
      } else {
        showLoadError();
      }
      return;
    }

    image.addEventListener('load', finishAfterDecode, { once: true });
    image.addEventListener('error', showLoadError, { once: true });
    mediaLoadTimer = window.setTimeout(() => {
      if (mediaLoadSequence !== expectedSequence || image.complete) {
        return;
      }

      card.classList.add('is-media-loading');
      card.setAttribute('aria-busy', 'true');
    }, MEDIA_DIALOG_LOADING.revealDelay);
  };

  const getSharedTransform = (from, to) => {
    if (!from.width || !from.height || !to.width || !to.height) {
      return 'translate3d(0, 0, 0) scale(1, 1)';
    }

    const translateX = from.left - to.left;
    const translateY = from.top - to.top;
    const scaleX = from.width / to.width;
    const scaleY = from.height / to.height;

    return `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`;
  };

  const getControlCounterScale = (from, to) =>
    Math.max(
      MEDIA_DIALOG_MOTION.minimumCounterScale,
      Math.min(MEDIA_DIALOG_MOTION.maximumCounterScale, to.width / from.width),
    );

  const waitForAnimations = (expectedSequence, onFinish) => {
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      if (sequence === expectedSequence) {
        onFinish();
      }
    });
  };

  const finishOpen = () => {
    if (state !== 'opening') {
      return;
    }

    state = 'open';
    cancelAnimations();
    delete dialog.dataset.mediaDialogState;
  };

  const finishClose = () => {
    cancelMediaSwap();
    state = 'closed';
    delete dialog.dataset.mediaDialogState;
    delete closeButton.dataset.suppressFocusRing;
    trigger.setAttribute('aria-expanded', 'false');

    if (dialog.open) {
      dialog.close();
    }

    cancelAnimations();
    clearMediaLoadState();

    if (activeTrigger?.getClientRects().length) {
      activeTrigger.focus({ preventScroll: true });
    }
    activeTrigger = null;
  };

  const openDialog = (event) => {
    if (state !== 'closed' || !fullscreenMedia.matches) {
      return;
    }

    state = 'opening';
    const expectedSequence = ++sequence;
    activeTrigger = trigger;
    const sourceRect = source.getBoundingClientRect();
    const sourceRadius = getComputedStyle(source).borderRadius;

    dialog.showModal();
    dialog.dataset.mediaDialogState = 'opening';
    trigger.setAttribute('aria-expanded', 'true');
    prepareDialogMedia();
    if (event?.detail > 0) {
      closeButton.dataset.suppressFocusRing = '';
    }
    closeButton.focus({ preventScroll: true });

    if (reducedMotion.matches) {
      animations = navigationButtons.map((button) =>
        button.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: MEDIA_DIALOG_MOTION.navigationEnterDuration,
          easing: MEDIA_DIALOG_MOTION.openEasing,
          fill: 'both',
        }),
      );

      if (animations.length) {
        waitForAnimations(expectedSequence, finishOpen);
      } else {
        finishOpen();
      }
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const dialogRadius = getComputedStyle(card).borderRadius;
    const sharedTransform = getSharedTransform(sourceRect, cardRect);
    const controlCounterScale = getControlCounterScale(sourceRect, cardRect);
    const timing = {
      duration: MEDIA_DIALOG_MOTION.openDuration,
      easing: MEDIA_DIALOG_MOTION.openEasing,
      fill: 'both',
    };

    animations = [
      card.animate(
        [
          { borderRadius: sourceRadius, transform: sharedTransform },
          { borderRadius: dialogRadius, transform: 'translate3d(0, 0, 0) scale(1, 1)' },
        ],
        timing,
      ),
      animateBackdrop([{ opacity: 0 }, { opacity: 1 }], timing),
      closeButton.animate(
        [
          {
            opacity: MEDIA_DIALOG_MOTION.lensSharedOpacity,
            transform: `scale(${controlCounterScale})`,
          },
          { opacity: MEDIA_DIALOG_MOTION.lensRestingOpacity, transform: 'scale(1)' },
        ],
        timing,
      ),
      closeIcon.animate(
        [
          {
            opacity: 0,
            filter: `blur(${MEDIA_DIALOG_MOTION.iconBlur}px)`,
            transform: `scale(${MEDIA_DIALOG_MOTION.iconScale})`,
          },
          { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' },
        ],
        {
          duration: MEDIA_DIALOG_MOTION.iconDuration,
          delay: MEDIA_DIALOG_MOTION.iconDelay,
          easing: MEDIA_DIALOG_MOTION.openEasing,
          fill: 'both',
        },
      ),
      ...navigationButtons.map((button) =>
        button.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: MEDIA_DIALOG_MOTION.navigationEnterDuration,
          delay: MEDIA_DIALOG_MOTION.openDuration,
          easing: MEDIA_DIALOG_MOTION.openEasing,
          fill: 'both',
        }),
      ),
    ];

    waitForAnimations(expectedSequence, finishOpen);
  };

  const closeDialog = () => {
    if (state === 'closed' || state === 'closing') {
      return;
    }

    cancelMediaSwap();
    state = 'closing';
    const expectedSequence = ++sequence;
    dialog.dataset.mediaDialogState = 'closing';
    trigger.setAttribute('aria-expanded', 'false');

    if (reducedMotion.matches) {
      animations = navigationButtons.map((button) =>
        button.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: MEDIA_DIALOG_MOTION.navigationExitDuration,
          easing: MEDIA_DIALOG_MOTION.closeEasing,
          fill: 'both',
        }),
      );

      if (animations.length) {
        waitForAnimations(expectedSequence, finishClose);
      } else {
        finishClose();
      }
      return;
    }

    if (animations.length) {
      animations.forEach((animation) => animation.reverse());
      waitForAnimations(expectedSequence, finishClose);
      return;
    }

    const sourceRect = source.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const sourceRadius = getComputedStyle(source).borderRadius;
    const dialogRadius = getComputedStyle(card).borderRadius;
    const sharedTransform = getSharedTransform(sourceRect, cardRect);
    const controlCounterScale = getControlCounterScale(sourceRect, cardRect);
    const timing = {
      duration: MEDIA_DIALOG_MOTION.closeDuration,
      easing: MEDIA_DIALOG_MOTION.closeEasing,
      fill: 'both',
    };

    animations = [
      card.animate(
        [
          { borderRadius: dialogRadius, transform: 'translate3d(0, 0, 0) scale(1, 1)' },
          { borderRadius: sourceRadius, transform: sharedTransform },
        ],
        timing,
      ),
      animateBackdrop([{ opacity: 1 }, { opacity: 0 }], timing),
      closeButton.animate(
        [
          { opacity: MEDIA_DIALOG_MOTION.lensRestingOpacity, transform: 'scale(1)' },
          {
            opacity: MEDIA_DIALOG_MOTION.lensSharedOpacity,
            transform: `scale(${controlCounterScale})`,
          },
        ],
        timing,
      ),
      closeIcon.animate(
        [
          { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' },
          {
            opacity: 0,
            filter: `blur(${MEDIA_DIALOG_MOTION.iconBlur}px)`,
            transform: `scale(${MEDIA_DIALOG_MOTION.iconScale})`,
          },
        ],
        {
          duration: MEDIA_DIALOG_MOTION.iconDuration,
          easing: MEDIA_DIALOG_MOTION.closeEasing,
          fill: 'both',
        },
      ),
      ...navigationButtons.map((button) =>
        button.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: MEDIA_DIALOG_MOTION.navigationExitDuration,
          easing: MEDIA_DIALOG_MOTION.closeEasing,
          fill: 'both',
        }),
      ),
    ];

    waitForAnimations(expectedSequence, finishClose);
  };

  trigger.addEventListener('click', openDialog);
  source.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (event.defaultPrevented || event.button !== 0 || target?.closest('[data-product-media-dialog-open]')) {
      return;
    }

    openDialog(event);
  });
  closeButton.addEventListener('click', closeDialog);
  previousButton?.addEventListener('click', () => showAdjacentMedia(-1));
  nextButton?.addEventListener('click', () => showAdjacentMedia(1));
  backdrop.addEventListener('click', closeDialog);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === backdrop) {
      closeDialog();
    }
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });
  dialog.addEventListener('keydown', (event) => {
    delete closeButton.dataset.suppressFocusRing;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key === 'ArrowLeft' && dialogMedia.length > 1) {
      event.preventDefault();
      showAdjacentMedia(-1);
    } else if (event.key === 'ArrowRight' && dialogMedia.length > 1) {
      event.preventDefault();
      showAdjacentMedia(1);
    }
  });
  dialog.addEventListener('close', () => {
    if (state !== 'closed') {
      finishClose();
    }
  });
  root.addEventListener('sigma:product-media-change', () => {
    if (dialog.open) {
      prepareDialogMedia();
    }
  });
  fullscreenMedia.addEventListener('change', (event) => {
    if (!event.matches && state !== 'closed') {
      sequence += 1;
      finishClose();
    }
  });

  root.classList.add('has-media-dialog');
};

const initializeStickyPurchasePanel = (root) => {
  const panel = root.querySelector('[data-sticky-purchase]');
  const sentinel = root.querySelector('[data-sticky-purchase-sentinel]');

  if (!panel || !sentinel) {
    return;
  }

  const closeButton = panel.querySelector('[data-sticky-purchase-close]');
  const dragHandle = panel.querySelector('[data-sticky-purchase-handle]');
  const sourceError = root.querySelector('[data-product-form-error]');
  const stickyError = panel.querySelector('[data-sticky-purchase-error]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopPurchaseLayout = window.matchMedia('(min-width: 1024px)');
  const hasBundleOffer = Boolean(root.querySelector('[data-bundle-offer]'));
  panel.dataset.stickyPurchaseDesktop = String(hasBundleOffer);
  let dismissed = false;
  let commitFrame = 0;
  let dragState;
  let hideTimer = 0;
  let suppressHandleClick = false;
  let updateFrame;

  const commitHidden = () => {
    window.clearTimeout(hideTimer);
    window.cancelAnimationFrame(commitFrame);
    hideTimer = 0;
    panel.dataset.stickyPurchaseCommit = 'true';
    panel.dataset.stickyPurchaseStage = String(STICKY_PURCHASE_STAGE.hidden);
    delete panel.dataset.stickyPurchaseExiting;
    panel.style.removeProperty('--sticky-purchase-exit-y');
    commitFrame = window.requestAnimationFrame(() => {
      commitFrame = window.requestAnimationFrame(() => {
        delete panel.dataset.stickyPurchaseCommit;
        commitFrame = 0;
      });
    });
  };

  const setVisible = (visible) => {
    panel.setAttribute('aria-hidden', String(!visible));
    panel.inert = !visible;

    if (visible) {
      window.clearTimeout(hideTimer);
      window.cancelAnimationFrame(commitFrame);
      hideTimer = 0;
      commitFrame = 0;
      delete panel.dataset.stickyPurchaseCommit;
      delete panel.dataset.stickyPurchaseExiting;
      panel.style.removeProperty('--sticky-purchase-exit-y');
      panel.dataset.stickyPurchaseStage = String(STICKY_PURCHASE_STAGE.visible);
      return;
    }

    const isVisible =
      panel.dataset.stickyPurchaseStage === String(STICKY_PURCHASE_STAGE.visible);

    if (
      !isVisible ||
      reducedMotion.matches ||
      panel.dataset.stickyPurchaseReady !== 'true'
    ) {
      commitHidden();
      return;
    }

    if (panel.dataset.stickyPurchaseExiting === 'true') {
      return;
    }

    panel.dataset.stickyPurchaseExiting = 'true';
    hideTimer = window.setTimeout(commitHidden, STICKY_PURCHASE_MOTION.exitDuration);
  };

  const dismissPanel = () => {
    dismissed = true;

    if (panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    setVisible(false);
  };

  const clearDragPosition = () => {
    panel.style.removeProperty('--sticky-purchase-drag-y');
  };

  const finishDrag = (event, { cancelled = false } = {}) => {
    if (!dragState || event.pointerId !== dragState.pointerId) {
      return;
    }

    const offset = Math.max(0, event.clientY - dragState.startY);
    const elapsed = Math.max(1, event.timeStamp - dragState.lastTime);
    const velocity = Math.max(0, (event.clientY - dragState.lastY) / elapsed);
    const distanceThreshold = Math.min(
      STICKY_PURCHASE_DRAG.maximumDistance,
      panel.getBoundingClientRect().height * STICKY_PURCHASE_DRAG.distanceRatio,
    );
    const shouldDismiss =
      !cancelled &&
      (offset >= distanceThreshold ||
        (offset >= STICKY_PURCHASE_DRAG.minimumFlingDistance &&
          velocity >= STICKY_PURCHASE_DRAG.velocityThreshold));

    window.removeEventListener('pointermove', moveDrag);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', cancelDrag);
    dragState = undefined;
    delete panel.dataset.stickyPurchaseDragging;

    if (!shouldDismiss) {
      clearDragPosition();
      return;
    }

    suppressHandleClick = true;
    window.setTimeout(() => {
      suppressHandleClick = false;
    }, 0);
    window.requestAnimationFrame(() => {
      panel.style.setProperty(
        '--sticky-purchase-exit-y',
        `${offset + STICKY_PURCHASE_MOTION.exitDistance}px`,
      );
      clearDragPosition();
      dismissPanel();
    });
  };

  const moveDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) {
      return;
    }

    event.preventDefault();
    const offset = Math.max(0, event.clientY - dragState.startY);
    panel.style.setProperty('--sticky-purchase-drag-y', `${offset}px`);
    dragState.lastY = event.clientY;
    dragState.lastTime = event.timeStamp;
  };

  const endDrag = (event) => finishDrag(event);
  const cancelDrag = (event) => finishDrag(event, { cancelled: true });

  const updateVisibility = () => {
    updateFrame = undefined;

    if (desktopPurchaseLayout.matches && !hasBundleOffer) {
      setVisible(false);
      return;
    }

    const header = document.querySelector('.site-header');
    const headerBottom = Math.max(
      0,
      header?.getBoundingClientRect().bottom || 0,
      header?.offsetHeight || 0,
    );
    const isVisible =
      panel.dataset.stickyPurchaseStage === String(STICKY_PURCHASE_STAGE.visible);
    const visibilityThreshold =
      headerBottom +
      STICKY_PURCHASE_STAGE.revealOffset +
      (isVisible ? STICKY_PURCHASE_STAGE.hideHysteresis : 0);
    const passedPurchaseFlow =
      sentinel.getBoundingClientRect().top <= visibilityThreshold;

    setVisible(passedPurchaseFlow && !dismissed);
  };

  const requestVisibilityUpdate = () => {
    if (!updateFrame) {
      updateFrame = window.requestAnimationFrame(updateVisibility);
    }
  };

  closeButton?.addEventListener('click', dismissPanel);

  dragHandle?.addEventListener('pointerdown', (event) => {
    if (
      event.button !== 0 ||
      desktopPurchaseLayout.matches ||
      panel.dataset.stickyPurchaseStage !== String(STICKY_PURCHASE_STAGE.visible)
    ) {
      return;
    }

    dragState = {
      pointerId: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
    };
    panel.dataset.stickyPurchaseDragging = 'true';
    window.addEventListener('pointermove', moveDrag, { passive: false });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', cancelDrag);
  });
  dragHandle?.addEventListener('click', () => {
    if (!suppressHandleClick) {
      dismissPanel();
    }
  });
  dragHandle?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      dismissPanel();
    }
  });

  if (sourceError && stickyError && 'MutationObserver' in window) {
    const syncError = () => {
      const message = sourceError.textContent.trim();
      const visible = Boolean(message) && !sourceError.hidden;

      stickyError.textContent = visible ? message : '';
      stickyError.hidden = !visible;
    };

    const errorObserver = new MutationObserver(syncError);
    errorObserver.observe(sourceError, {
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    syncError();
  }

  updateVisibility();
  window.requestAnimationFrame(() => {
    panel.dataset.stickyPurchaseReady = 'true';
  });
  window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
  window.addEventListener('resize', requestVisibilityUpdate);
  desktopPurchaseLayout.addEventListener('change', requestVisibilityUpdate);
};

const initializeProductDescription = (root) => {
  const section = root.querySelector('[data-product-description]');
  const content = section?.querySelector('[data-product-description-content]');
  const toggle = section?.querySelector('[data-product-description-toggle]');
  const iconPaths = [...(toggle?.querySelectorAll('[data-path-show][data-path-hide]') || [])];
  const labels = toggle?.querySelector('.coffee-product__description-toggle-labels');
  const moreLabel = toggle?.querySelector('.coffee-product__description-toggle-label--more');
  const lessLabel = toggle?.querySelector('.coffee-product__description-toggle-label--less');

  if (!section || !content || !toggle) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsPathMorph = window.CSS?.supports?.('d', 'path("M0 0L1 1")') === true;
  let animation;
  let collapsedHeight = DESCRIPTION_MOTION.targetCollapsedHeight;
  let resizeFrame;

  const syncToggleLabelWidths = () => {
    if (toggle.hidden || !labels || !moreLabel || !lessLabel) {
      return;
    }

    labels.style.setProperty('--description-toggle-label-width-more', `${moreLabel.scrollWidth}px`);
    labels.style.setProperty('--description-toggle-label-width-less', `${lessLabel.scrollWidth}px`);
  };

  const setFallbackIconState = (expanded) => {
    if (supportsPathMorph) {
      return;
    }

    iconPaths.forEach((path) => {
      path.setAttribute('d', expanded ? path.dataset.pathHide : path.dataset.pathShow);
    });
  };

  const setToggleState = (expanded) => {
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.setAttribute(
      'aria-label',
      expanded ? 'Collapse product description' : 'Expand product description',
    );
    setFallbackIconState(expanded);
  };

  const measureCollapsedHeight = () => {
    const fullHeight = content.scrollHeight;
    const targetHeight = Math.min(DESCRIPTION_MOTION.targetCollapsedHeight, fullHeight);
    const minimumSnappedHeight = targetHeight - DESCRIPTION_MOTION.blockSnapRange;
    const contentTop = content.getBoundingClientRect().top;
    const blockBoundaries = [...content.children]
      .map((child) => child.getBoundingClientRect().bottom - contentTop)
      .filter((boundary) => boundary >= minimumSnappedHeight && boundary <= targetHeight);
    const snappedHeight = blockBoundaries.at(-1);

    return {
      collapsedHeight: Math.max(0, Math.min(snappedHeight ?? targetHeight, fullHeight)),
      fullHeight,
    };
  };

  const revealToggleWhenNeeded = () => {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const measurement = measureCollapsedHeight();
    collapsedHeight = measurement.collapsedHeight;
    const needsToggle =
      measurement.fullHeight - collapsedHeight >= DESCRIPTION_MOTION.minimumOverflow;

    content.style.setProperty('--description-collapsed-height', `${collapsedHeight}px`);
    toggle.hidden = !needsToggle;
    section.classList.toggle('is-collapsible', needsToggle);

    if (!needsToggle) {
      content.dataset.descriptionExpanded = 'true';
      content.style.removeProperty('height');
      return;
    }

    if (!isExpanded) {
      content.dataset.descriptionExpanded = 'false';
    }
  };

  const restoreTogglePosition = (previousTop) => {
    if (!Number.isFinite(previousTop)) {
      return;
    }

    window.requestAnimationFrame(() => {
      const offset = toggle.getBoundingClientRect().top - previousTop;

      if (Math.abs(offset) > 1) {
        window.scrollBy({ top: offset, left: 0, behavior: 'auto' });
      }
    });
  };

  const finishAtState = (expanded, previousToggleTop) => {
    animation = undefined;
    content.dataset.descriptionExpanded = String(expanded);
    content.style.removeProperty('height');

    if (!expanded) {
      restoreTogglePosition(previousToggleTop);
    }
  };

  const animateToState = (expanded) => {
    const startHeight = content.getBoundingClientRect().height;
    const previousToggleTop = expanded ? undefined : toggle.getBoundingClientRect().top;

    if (animation) {
      animation.cancel();
      animation = undefined;
    }

    content.style.height = `${startHeight}px`;
    content.dataset.descriptionExpanded = 'true';
    const endHeight = expanded ? content.scrollHeight : collapsedHeight;

    setToggleState(expanded);

    if (reducedMotion.matches || !content.animate || Math.abs(endHeight - startHeight) < 1) {
      finishAtState(expanded, previousToggleTop);
      return;
    }

    animation = content.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      {
        duration: DESCRIPTION_MOTION.duration,
        easing: DESCRIPTION_MOTION.easing,
      },
    );

    animation.addEventListener(
      'finish',
      () => finishAtState(expanded, previousToggleTop),
      { once: true },
    );
  };

  toggle.addEventListener('click', () => {
    animateToState(toggle.getAttribute('aria-expanded') !== 'true');
  });

  revealToggleWhenNeeded();
  syncToggleLabelWidths();

  document.fonts?.ready.then(syncToggleLabelWidths);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => {
      if (!animation) {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(revealToggleWhenNeeded);
      }
    });
    observer.observe(content);
  }
};

const DISCOVERY_RAIL_CONTROLLER = Symbol('sigmaDiscoveryRailController');

const initializeDiscoveryRail = (section) => {
  if (!(section instanceof HTMLElement)) {
    return;
  }

  if (section[DISCOVERY_RAIL_CONTROLLER]) {
    section[DISCOVERY_RAIL_CONTROLLER].refresh();
    return;
  }

  const viewport = section.querySelector('[data-product-discovery-viewport]');
  const rail = section.querySelector('.coffee-product__discovery-rail');
  const controls = section.querySelector('[data-product-discovery-controls]');
  const previous = section.querySelector('[data-product-discovery-previous]');
  const next = section.querySelector('[data-product-discovery-next]');
  const status = section.querySelector('[data-product-discovery-status]');

  if (
    !(viewport instanceof HTMLElement) ||
    !(rail instanceof HTMLElement) ||
    !(controls instanceof HTMLElement) ||
    !(previous instanceof HTMLButtonElement) ||
    !(next instanceof HTMLButtonElement) ||
    !(status instanceof HTMLElement)
  ) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let updateFrame = 0;
  let statusTimer = 0;

  const cards = () =>
    [...rail.children].filter((card) => card instanceof HTMLElement && !card.hidden);

  const cardOffset = (card) => card.offsetLeft - rail.offsetLeft;

  const visibleRange = (items) => {
    const left = viewport.scrollLeft + 1;
    const right = left + viewport.clientWidth - 2;
    const visible = items.filter((card) => {
      const cardLeft = cardOffset(card);
      const cardRight = cardLeft + card.offsetWidth;
      return cardRight > left && cardLeft < right;
    });

    if (!visible.length) {
      return { first: 0, last: 0 };
    }

    return {
      first: items.indexOf(visible[0]),
      last: items.indexOf(visible[visible.length - 1]),
    };
  };

  const update = ({ announce = false } = {}) => {
    updateFrame = 0;
    const items = cards();
    const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const hasOverflow = maximumScroll > 1 && items.length > 1;

    controls.hidden = !hasOverflow;
    previous.disabled = !hasOverflow || viewport.scrollLeft <= 1;
    next.disabled = !hasOverflow || viewport.scrollLeft >= maximumScroll - 1;

    if (!items.length) {
      status.textContent = '';
      return;
    }

    const range = visibleRange(items);
    const nextStatus = `${range.first + 1}–${range.last + 1} of ${items.length}`;

    if (status.textContent === nextStatus) {
      return;
    }

    if (!announce) {
      status.textContent = nextStatus;
      return;
    }

    window.clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => {
      status.textContent = nextStatus;
    }, 140);
  };

  const requestUpdate = ({ announce = false } = {}) => {
    if (updateFrame) {
      return;
    }

    updateFrame = window.requestAnimationFrame(() => update({ announce }));
  };

  const visibleCardCount = (items) => {
    if (!items.length) {
      return 1;
    }

    const styles = window.getComputedStyle(rail);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return Math.max(
      1,
      Math.floor((viewport.clientWidth + gap) / (items[0].offsetWidth + gap)),
    );
  };

  const move = (direction) => {
    const items = cards();
    if (!items.length) {
      return;
    }

    const currentIndex = items.reduce((closestIndex, card, index) => {
      const closestDistance = Math.abs(cardOffset(items[closestIndex]) - viewport.scrollLeft);
      const nextDistance = Math.abs(cardOffset(card) - viewport.scrollLeft);
      return nextDistance < closestDistance ? index : closestIndex;
    }, 0);
    const targetIndex = Math.max(
      0,
      Math.min(items.length - 1, currentIndex + direction * visibleCardCount(items)),
    );

    viewport.scrollTo({
      left: cardOffset(items[targetIndex]),
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  };

  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  viewport.addEventListener('scroll', () => requestUpdate({ announce: true }), { passive: true });
  viewport.addEventListener('keydown', (event) => {
    if (event.target !== viewport) {
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      viewport.scrollTo({
        left: event.key === 'Home' ? 0 : viewport.scrollWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
  });

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => requestUpdate());
    observer.observe(viewport);
    observer.observe(rail);
  }

  section[DISCOVERY_RAIL_CONTROLLER] = {
    refresh: () => requestUpdate(),
  };
  update();
};

const initializeDiscoveryRails = (root) => {
  root.querySelectorAll('[data-product-discovery-section]').forEach(initializeDiscoveryRail);
};

const initializeRecentlyViewed = (root) => {
  const section = root.querySelector('[data-recently-viewed]');
  const rail = root.querySelector('[data-recently-viewed-rail]');
  const productSource = root.querySelector('[data-recently-viewed-product]');
  const cardTemplate = root.querySelector('[data-recently-viewed-card-template]');

  if (!section || !rail || !productSource || !cardTemplate) {
    return;
  }

  let currentProduct;

  try {
    currentProduct = JSON.parse(productSource.textContent);
  } catch {
    return;
  }

  if (!currentProduct?.id || !currentProduct?.url || !currentProduct?.title) {
    return;
  }

  let storedProducts = [];

  try {
    const storedValue = JSON.parse(
      localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) ||
        localStorage.getItem(LEGACY_RECENTLY_VIEWED_STORAGE_KEY) ||
        '[]',
    );
    if (Array.isArray(storedValue)) {
      storedProducts = storedValue;
    }
  } catch {
    storedProducts = [];
  }

  const recentlyViewedProducts = storedProducts
    .filter(
      (item, index, items) =>
        item?.id &&
        String(item.id) !== String(currentProduct.id) &&
        items.findIndex((candidate) => String(candidate?.id) === String(item.id)) === index,
    )
    .slice(0, RECENTLY_VIEWED_RENDER_LIMIT);

  recentlyViewedProducts.forEach((item) => {
    const card = cardTemplate.content.cloneNode(true);
    const image = card.querySelector('[data-recently-viewed-image]');
    const title = card.querySelector('[data-recently-viewed-title]');
    const notes = card.querySelector('[data-recently-viewed-notes]');
    const weights = card.querySelector('[data-recently-viewed-weights]');
    const price = card.querySelector('[data-recently-viewed-price]');
    const link = card.querySelector('[data-recently-viewed-link]');
    const imageExpand = card.querySelector('[data-recently-viewed-image-expand]');

    if (item.image) {
      image.src = item.image;
      image.srcset = `${item.image} 1x`;
    }
    if (imageExpand) {
      imageExpand.dataset.productDiscoveryImageSrc = item.imagePreview || item.image || image.src;
      imageExpand.dataset.productDiscoveryImageTitle = item.title || '';
      imageExpand.setAttribute(
        'aria-label',
        `Expand image for ${item.accessibleTitle || item.title}`,
      );
    }
    title.textContent = item.title || '';
    notes.textContent = (item.notes || '').replaceAll(' - ', ' • ');
    notes.hidden = !item.notes;
    weights.textContent = item.weights || '';
    weights.hidden = !item.weights;
    price.textContent = plainTextFromMarkup(getRecentlyViewedPrice(item, currentProduct.priceContext));
    price.hidden = !price.textContent;
    link.href = item.url;
    link.setAttribute('aria-label', `View ${item.accessibleTitle || item.title}`);
    rail.append(card);
  });

  if (recentlyViewedProducts.length) {
    section.hidden = false;
    root.querySelector('[data-product-discovery]')?.removeAttribute('hidden');
  }

  const nextStoredProducts = [
    currentProduct,
    ...storedProducts.filter((item) => String(item?.id) !== String(currentProduct.id)),
  ].slice(0, RECENTLY_VIEWED_LIMIT);

  try {
    localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(nextStoredProducts));
  } catch {
    // Recently viewed remains an optional enhancement when storage is unavailable.
  }
};

const initializeProduct = (root) => {
  if (root.dataset.productEnhanced === 'true') {
    return;
  }

  initializeFellowBreadcrumb(root);

  const variants = parseVariants(root);

  if (!variants.length) {
    return;
  }

  const optionOrderChanged = sortProductOptionValues(root, variants);
  root.dataset.productEnhanced = 'true';

  if (root.hasAttribute('data-library-card-product')) {
    const productForm = root.querySelector('.coffee-product__form');
    productForm?.addEventListener('submit', (event) => {
      const planInput = productForm.querySelector('[data-library-card-selling-plan]');
      if (planInput?.value) return;

      event.preventDefault();
      const formError = productForm.querySelector('[data-product-form-error]');
      if (formError) {
        formError.textContent = 'This monthly top-up is temporarily unavailable. Please refresh and try again.';
        formError.hidden = false;
      }
    });
  }

  if (optionOrderChanged) {
    window.requestAnimationFrame(() => root.classList.add('is-enhanced'));
  } else {
    root.classList.add('is-enhanced');
  }

  const quantityPicker = initializeQuantityPicker(root, (quantity, { animate = true } = {}) => {
    const variant = resolveSelectedVariant(root, variants);
    updateAddButtonTotal(root, variant, quantity, { animate });
    updateStickyPurchaseQuantity(root, quantity, { animate });
    root.dispatchEvent(
      new CustomEvent('sigma:product-quantity-change', {
        detail: { quantity },
      }),
    );
  });

  root.addEventListener('sigma:bundle-purchase-total', (event) => {
    const formattedTotal = String(event.detail?.formattedTotal || '').trim();
    const selectedCount = Math.max(0, Number(event.detail?.selectedCount) || 0);
    const animate = event.detail?.animate === true;

    if (!formattedTotal) {
      return;
    }

    root
      .querySelectorAll(
        '[data-add-to-bag-price], [data-sticky-add-to-bag-price], [data-sticky-purchase-price]',
      )
      .forEach((price) => updateSwappingText(price, formattedTotal, { animate }));

    const stickyPurchaseTitle = root.querySelector('[data-sticky-purchase-title]');
    const stickyPurchaseBaseTitle = String(
      stickyPurchaseTitle?.dataset.stickyPurchaseBaseTitle || '',
    ).trim();

    if (stickyPurchaseBaseTitle) {
      updateSwappingText(
        stickyPurchaseTitle,
        selectedCount > 0 ? `${stickyPurchaseBaseTitle} - Bundle` : stickyPurchaseBaseTitle,
        { animate },
      );
    }

    [
      root.querySelector('[data-add-to-bag-text]'),
      root.querySelector('[data-sticky-add-to-bag-text]'),
    ].forEach((label) => {
      if (root.hasAttribute('data-library-card-product')) {
        updateSwappingText(label, getAvailablePurchaseLabel(root), { animate });
        return;
      }
      updateBundlePurchaseLabel(label, selectedCount, { animate });
    });
  });

  root.addEventListener('sigma:product-variant-sold-out', (event) => {
    const soldOutVariantId = String(event.detail?.variantId || '');
    const variant = variants.find((candidate) => String(candidate.id) === soldOutVariantId);

    if (!variant) {
      return;
    }

    variant.available = false;
    variant.inventoryQuantity = 0;
    updateProductState(root, variants, {
      animatePrice: false,
      updateUrl: false,
      preserveSelection: true,
    });
    if (root.hasAttribute('data-private-mystery-hold')) {
      void syncPrivateMysteryProductAvailability(root, soldOutVariantId);
    }
  });

  root.querySelectorAll('[data-option-input]').forEach((input) => {
    input.addEventListener('change', () => {
      quantityPicker?.reset({ notify: false });
      updateProductState(root, variants);
      const selectedVariantId = root.querySelector('[data-variant-select]')?.value;
      void syncPrivateMysteryProductAvailability(root, selectedVariantId);
    });
  });
  initializeGallery(root);
  initializeAdaptiveMediaControls(root);
  initializeMediaDialog(root);
  initializeStickyPurchasePanel(root);
  initializeProductDescription(root);
  initializeRecentlyViewed(root);
  initializeDiscoveryRails(root);
  updateProductState(root, variants, { animatePrice: false, updateUrl: false });
  void syncPrivateMysteryProductAvailability(
    root,
    root.querySelector('[data-variant-select]')?.value,
  );
  root.dispatchEvent(new CustomEvent('sigma:product-ready', { bubbles: true }));
};

const initializeProducts = (scope = document) => {
  scope.querySelectorAll(PRODUCT_SELECTOR).forEach(initializeProduct);
};

initializeProducts();

document.addEventListener('shopify:section:load', (event) => {
  initializeProducts(event.target);
});
